import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-570">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-571">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-572">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-573">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-574" /> : <Plus className="w-4 h-4" data-id="element-575" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-576">
        <p className="text-gray-600 leading-relaxed" data-id="element-577">{answer}</p>
      </div>
    </div>;
}
export function ChooseDentistArticle() {
  const {
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pick = (en: string, ua: string, ru: string, ro: string) => {
    if (lang === 'ua') return ua;
    if (lang === 'ru') return ru;
    if (lang === 'ro') return ro;
    return en;
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: pick('How to check if a dentist works legally in the UK?', 'Як перевірити, чи лікар легально працює в UK?', 'Как проверить, работает ли врач легально в UK?', 'Cum să verificați dacă un dentist lucrează legal în Marea Britanie?'),
    a: pick("Every dentist must be registered with the GDC (General Dental Council). You can check the doctor's name on the GDC website.", "Кожен стоматолог повинен бути зареєстрований в GDC (General Dental Council). Ви можете перевірити ім'я лікаря на сайті GDC.", 'Каждый стоматолог должен быть зарегистрирован в GDC (General Dental Council). Вы можете проверить имя врача на сайте GDC.', 'Fiecare dentist trebuie să fie înregistrat la GDC (General Dental Council). Puteți verifica numele medicului pe site-ul GDC.')
  }, {
    q: pick('Are Google reviews important?', 'Чи важливі відгуки в Google?', 'Важны ли отзывы в Google?', 'Sunt importante recenziile pe Google?'),
    a: pick('Yes, they are one of the best indicators. Pay attention not only to the rating but also to the text of the reviews — what exactly patients write about.', 'Так, це один з найкращих показників. Звертайте увагу не тільки на оцінку, а й на текст відгуків — про що саме пишуть пацієнти.', 'Да, это один из лучших показателей. Обращайте внимание не только на оценку, но и на текст отзывов — о чем именно пишут пациенты.', 'Da, sunt unul dintre cei mai buni indicatori. Acordați atenție nu doar ratingului, ci și textului recenziilor — despre ce scriu exact pacienții.')
  }, {
    q: pick('Why is Angel / Islington a convenient area?', 'Чому Angel / Islington — зручний район?', 'Почему Angel / Islington — удобный район?', 'De ce Angel / Islington este o zonă convenabilă?'),
    a: pick('It is Central London (Zone 1), easily accessible by tube (Northern Line) or bus. There are many offices and cafes here, making it easy to combine a visit with other errands.', 'Це центральний Лондон (Zone 1), сюди легко дістатися на метро (Northern Line) або автобусом. Тут багато офісів та кафе, зручно поєднати візит з іншими справами.', 'Это центральный Лондон (Zone 1), сюда легко добраться на метро (Northern Line) или автобусом. Здесь много офисов и кафе, удобно совместить визит с другими делами.', 'Este Londra Centrală (Zona 1), ușor accesibilă cu metroul (Northern Line) sau autobuzul. Există multe birouri și cafenele aici, fiind ușor să combinați o vizită cu alte comisioane.')
  }];
  return <Layout data-id="element-578">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-579" />

      <article className="bg-white" data-id="element-580">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-581">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-582">
            <Link to={`/${lang}/blog`} className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-583">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-584" />
              {pick('Back to blog', 'Назад до блогу', 'Назад в блог', 'Înapoi la blog')}
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-585">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-586">
                <Tag className="w-3 h-3 mr-2" data-id="element-587" />
                {pick('Tips', 'Поради', 'Советы', 'Sfaturi')}
              </span>
              <span className="flex items-center" data-id="element-588">
                <Calendar className="w-4 h-4 mr-2" data-id="element-589" />
                {pick('February 2026', 'Лютий 2026', 'Февраль 2026', 'Februarie 2026')}
              </span>
              <span className="flex items-center" data-id="element-590">
                <Clock className="w-4 h-4 mr-2" data-id="element-591" />
                {pick('7 min read', '7 хв читання', '7 мин чтения', '7 min citire')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-592">
              {pick('How to choose a dentist in Angel, Islington?', 'Як обрати стоматолога в Angel, Islington? Поради для українців', 'Как выбрать стоматолога в Angel, Islington? Советы', 'Cum să alegeți un dentist în Angel, Islington?')}
            </h1>

            <div className="flex items-center" data-id="element-593">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-594">
                DC
              </div>
              <div data-id="element-595">
                <p className="font-medium text-gray-900" data-id="element-596">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-597">
                  {pick('Expert Article', 'Експертна стаття', 'Экспертная статья', 'Articol Expert')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-598">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-599">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-600">
              {pick('Finding "your" doctor is difficult even at home, and in a new city, let alone another country, it is a real challenge. London is huge, and there are thousands of clinics here. How to choose the one where you will receive quality care, will not be harmed, and will not be overcharged?', 'Знайти "свого" лікаря важко навіть вдома, а в новому місті, та ще й в іншій країні — це справжній виклик. Лондон величезний, і клінік тут тисячі. Як обрати ту, де вам нададуть якісну допомогу, не нашкодять і не візьмуть зайвих грошей?', 'Найти "своего" врача трудно даже дома, а в новом городе, да еще и в другой стране — это настоящий вызов. Лондон огромен, и клиник здесь тысячи. Как выбрать ту, где вам окажут качественную помощь, не навредят и не возьмут лишних денег?', 'Găsirea medicului "tău" este dificilă chiar și acasă, iar într-un oraș nou, cu atât mai mult într-o altă țară, este o adevărată provocare. Londra este uriașă, iar aici sunt mii de clinici. Cum să o alegeți pe cea în care veți primi îngrijire de calitate, nu veți fi rănit și nu veți fi suprataxat?')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-601">
              {pick('Why Angel / Islington?', 'Чому Angel / Islington?', 'Почему Angel / Islington?', 'De ce Angel / Islington?')}
            </h2>
            <p className="mb-6" data-id="element-602">
              {pick('The Angel area (Islington) is one of the most convenient areas of Central London.', 'Район Angel (Islington) — це один з найзручніших районів центрального Лондона.', 'Район Angel (Islington) — это один из самых удобных районов центрального Лондона.', 'Zona Angel (Islington) este una dintre cele mai convenabile zone din Londra Centrală.')}
            </p>
            <ul className="space-y-2 mb-8" data-id="element-603">
              <li className="flex items-start" data-id="element-604">
                <MapPin className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-605" />
                <span data-id="element-606">
                  <strong data-id="element-607">
                    {pick('Transport:', 'Транспорт:', 'Транспорт:', 'Transport:')}
                  </strong>{' '}
                  {pick("Angel Station (Northern Line) allows you to quickly get from King's Cross, Bank, or London Bridge.", "Станція Angel (Northern Line) дозволяє швидко дістатися з King's Cross, Bank або London Bridge.", "Станция Angel (Northern Line) позволяет быстро добраться с King's Cross, Bank или London Bridge.", "Stația Angel (Northern Line) vă permite să ajungeți rapid de la King's Cross, Bank sau London Bridge.")}
                </span>
              </li>
              <li className="flex items-start" data-id="element-608">
                <MapPin className="w-5 h-5 text-teal-600 mr-2 mt-1 flex-shrink-0" data-id="element-609" />
                <span data-id="element-610">
                  <strong data-id="element-611">
                    {pick('Infrastructure:', 'Інфраструктура:', 'Инфраструктура:', 'Infrastructură:')}
                  </strong>{' '}
                  {pick('There are many offices, shops, and cafes here, so it is easy to fit a visit to the doctor into your schedule.', 'Тут багато офісів, магазинів та кафе, тому візит до лікаря легко вписати в графік.', 'Здесь много офисов, магазинов и кафе, поэтому визит к врачу легко вписать в график.', 'Aici există multe birouri, magazine și cafenele, așa că este ușor să încadrați o vizită la medic în programul dumneavoastră.')}
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-612">
              {pick('What to look for when choosing?', 'На що звертати увагу при виборі?', 'На что обращать внимание при выборе?', 'Ce să căutați când alegeți?')}
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-613">
              {pick('1. GDC Registration', '1. Реєстрація в GDC', '1. Регистрация в GDC', '1. Înregistrarea GDC')}
            </h3>
            <p className="mb-6" data-id="element-614">
              {pick('In the UK, every dentist, hygienist, and nurse must be registered with the General Dental Council (GDC). This is a guarantee that the doctor has the appropriate education and the right to practice.', 'У Великобританії кожен стоматолог, гігієніст та медсестра повинні бути зареєстровані в General Dental Council (GDC). Це гарантія того, що лікар має відповідну освіту і право на практику.', 'В Великобритании каждый стоматолог, гигиенист и медсестра должны быть зарегистрированы в General Dental Council (GDC). Это гарантия того, что врач имеет соответствующее образование и право на практику.', 'În Marea Britanie, fiecare dentist, igienist și asistentă medicală trebuie să fie înregistrat la General Dental Council (GDC). Aceasta este o garanție că medicul are educația corespunzătoare și dreptul de a profesa.')}
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-615">
              {pick('2. Patient Reviews', '2. Відгуки пацієнтів', '2. Отзывы пациентов', '2. Recenziile Pacienților')}
            </h3>
            <p className="mb-6" data-id="element-616">
              {pick('Google Reviews is the most honest indicator. Look for clinics with a rating of 4.8-5.0. Read what people actually write: was it painful? Were they satisfied with the result? Is the staff friendly?', 'Google Reviews — це найчесніший показник. Шукайте клініки з рейтингом 4.8-5.0. Читайте, що саме пишуть люди: чи було боляче? Чи були вони задоволені результатом? Чи привітний персонал?', 'Google Reviews — это самый честный показатель. Ищите клиники с рейтингом 4.8-5.0. Читайте, что именно пишут люди: было ли больно? Были ли они довольны результатом? Приветлив ли персонал?', 'Google Reviews este cel mai onest indicator. Căutați clinici cu un rating de 4.8-5.0. Citiți ce scriu oamenii de fapt: a fost dureros? Au fost mulțumiți de rezultat? Este personalul prietenos?')}
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-617">
              {pick('3. Equipment', '3. Обладнання', '3. Оборудование', '3. Echipament')}
            </h3>
            <p className="mb-6" data-id="element-618">
              {pick('Modern dentistry means digital technologies. A good clinic should have:', 'Сучасна стоматологія — це цифрові технології. Хороша клініка повинна мати:', 'Современная стоматология — это цифровые технологии. Хорошая клиника должна иметь:', 'Stomatologia modernă înseamnă tehnologii digitale. O clinică bună ar trebui să aibă:')}
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-619">
              <li data-id="element-620">
                {pick('Digital X-rays (less radiation).', 'Цифровий рентген (менше опромінення).', 'Цифровой рентген (меньше облучения).', 'Radiografii digitale (mai puțină radiație).')}
              </li>
              <li data-id="element-621">
                {pick('Intraoral cameras (to show you the condition of the tooth).', 'Інтраоральні камери (щоб показати вам стан зуба).', 'Интраоральные камеры (чтобы показать вам состояние зуба).', 'Camere intraorale (pentru a vă arăta starea dintelui).')}
              </li>
              <li data-id="element-622">
                {pick('Microscope (for root canal treatment).', 'Мікроскоп (для лікування каналів).', 'Микроскоп (для лечения каналов).', 'Microscop (pentru tratamentul de canal).')}
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-623">
              {pick('Why Dentico Clinic?', 'Чому Dentico Clinic?', 'Почему Dentico Clinic?', 'De ce Dentico Clinic?')}
            </h2>
            <p className="mb-6" data-id="element-624">
              {pick('We are proud to have created a clinic in Angel that meets the highest standards:', 'Ми пишаємося тим, що створили в Angel клініку, яка відповідає найвищим стандартам:', 'Мы гордимся тем, что создали в Angel клинику, которая соответствует самым высоким стандартам:', 'Suntem mândri că am creat o clinică în Angel care îndeplinește cele mai înalte standarde:')}
            </p>
            <ul className="space-y-3 mb-8" data-id="element-625">
              <li className="flex items-start" data-id="element-626">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-627" />
                <span data-id="element-628">
                  <strong data-id="element-629">
                    {pick('Experience:', 'Досвід:', 'Опыт:', 'Experiență:')}
                  </strong>{' '}
                  {pick('Our doctors have many years of experience working in the UK.', 'Наші лікарі мають багаторічний досвід роботи в UK.', 'Наши врачи имеют многолетний опыт работы в UK.', 'Medicii noștri au mulți ani de experiență de lucru în Marea Britanie.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-630">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-631" />
                <span data-id="element-632">
                  <strong data-id="element-633">
                    {pick('Reputation:', 'Репутація:', 'Репутация:', 'Reputație:')}
                  </strong>{' '}
                  {pick('We have excellent reviews from patients.', 'Ми маємо відмінні відгуки від пацієнтів.', 'Мы имеем отличные отзывы от пациентов.', 'Avem recenzii excelente de la pacienți.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-634">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-635" />
                <span data-id="element-636">
                  <strong data-id="element-637">
                    {pick('Comfort:', 'Комфорт:', 'Комфорт:', 'Confort:')}
                  </strong>{' '}
                  {pick('We speak English, Ukrainian, Russian, and Romanian.', 'Ми говоримо українською, російською та англійською.', 'Мы говорим на украинском, русском и английском.', 'Vorbim engleză, ucraineană, rusă și română.')}
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-638">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-639">
              {pick('Frequently Asked Questions', 'Часті запитання', 'Частые вопросы', 'Întrebări Frecvente')}
            </h3>
            <div className="space-y-2" data-id="element-640">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-641" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-642">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-643">
              {pick('Read Also', 'Читайте також', 'Читайте также', 'Citește și')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-644">
              <Link to={`/${lang}/about`} data-id="element-645">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-646">
                  <span className="text-teal-600 font-medium" data-id="element-647">
                    {pick('About Dentico Clinic', 'Про Dentico Clinic', 'О Dentico Clinic', 'Despre Dentico Clinic')}
                  </span>
                </Card>
              </Link>
              <Link to={`/${lang}/contact`} data-id="element-648">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-649">
                  <span className="text-teal-600 font-medium" data-id="element-650">
                    {pick('Contact Us', 'Наші контакти', 'Наши контакты', 'Contactați-ne')}
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-651">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-652">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-653">
              {pick('Visit us in Angel', 'Завітайте до нас в Angel', 'Приходите к нам в Angel', 'Vizitați-ne în Angel')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-654">
              {pick('We are located just 5 minutes from the tube station.', 'Ми знаходимось всього в 5 хвилинах від метро.', 'Мы находимся всего в 5 минутах от метро.', 'Ne aflăm la doar 5 minute de stația de metrou.')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-655">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-656">
                <Button variant="primaryInverse" size="lg" className="w-full gap-2" data-id="element-657">
                  <Calendar className="w-5 h-5" data-id="element-658" />
                  {pick('Book Online', 'Записатись онлайн', 'Записаться онлайн', 'Programare Online')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-659">
                <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-660">
                  <MessageCircle className="w-5 h-5" data-id="element-661" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}